import { useEffect, useState } from "react";
import { Row, Col, Form, FormGroup, Label, Input } from "reactstrap";
import CountrySelector from "./CountrySelector";

import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import StateSelector from "./StateSelector";
import { getJournalByIdApi } from "src/network/api";
import { IssuesSelector } from "src/utils/appUtils";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
export interface JournalData {
  journalId?: string;
  journal_name: string;
  image: File | null;
  eissn_no: string;
  pissn_no: string;
  first_volume: string;
  number_of_issue_per_year: string;
  publisher_name: string;
  website_link: string;
  journal_submission_link: string;
  broad_research_area: string;
  indexing: string;
  country: string;
  state: string;
  publication_type: string;
  usd_publication_charge: string;
  review_time: string;
  description: string;
  review_type: string;
  impact_factor: string; // New Field
  language: string; // New Field
  doi: string;
}

export const JournalForm = ({ journalId = null, onSubmit, submitButton }) => {
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState("");
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const {
    control,
    setValue,
    getValues,
    register,
    handleSubmit: handleFormSubmit,
    watch,
    formState: { errors },
  } = useForm<JournalData>({
    mode: "onChange",
    defaultValues: {
      journal_name: "",
      image: null,
      eissn_no: "",
      pissn_no: "",
      first_volume: "",
      number_of_issue_per_year: "",
      publisher_name: "",
      website_link: "",
      journal_submission_link: "",
      broad_research_area: "",
      indexing: "",
      country: "",
      state: "0",
      publication_type: "",
      usd_publication_charge: "",
      review_time: "",
      description: "",
      review_type: "",
      impact_factor: "", // New Field
      language: "", // New Field
      doi: "",
    },
  });

  const essinValue = watch("eissn_no");
  const pssinValue = watch("pissn_no");
  const publicationType = watch("publication_type");
  useEffect(() => {
    console.log("Journal ID:", journalId);
    if (journalId) {
      setLoading(true);
      const fetchJournal = async () => {
        try {
          const response = await getJournalByIdApi(journalId);
          if (response.status == 200 && response.data.status == 200) {
            const details: JournalData = response.data.data;
            let fValues = getValues();
            for (let key in fValues) {
              if (key in details) {
                setValue(key as keyof JournalData, details[key]);
              }
            }
          } else {
            toast.error("No data found for the given journal ID.");
          }
        } catch (error) {
          console.error("Error fetching journal:", error);
          toast.error("Error loading journal data.");
        } finally {
          setLoading(false);
        }
      };
      fetchJournal();
    }
    watch("country");
  }, [journalId]);
  if (loading) {
    return <p className="text-center">Please wait...</p>;
  }
  return (
    <Form onSubmit={handleFormSubmit(onSubmit)}>
      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="journal_name">Journal Name</Label>
            <Controller
              name="journal_name"
              control={control}
              rules={{
                required: "Journal Name is required",
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Enter journal name"
                  type="text"
                  id="journal_name"
                />
              )}
            />
            {errors.journal_name &&
              typeof errors.journal_name.message === "string" && (
                <div className="error-text">{errors.journal_name.message}</div>
              )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label for="eissn_no">E-ISSN Number</Label>
            <Controller
              name="eissn_no"
              control={control}
              rules={{
                required: pssinValue
                  ? false
                  : "E-ISSN Number OR P-ISSN Number is required",
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Enter E-ISSN number"
                  type="text"
                  id="eissn_no"
                />
              )}
            />
            {errors.eissn_no && typeof errors.eissn_no.message === "string" && (
              <div className="error-text">{errors.eissn_no.message}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label for="pissn_no">P-ISSN Number</Label>
            <Controller
              name="pissn_no"
              control={control}
              rules={{
                required: essinValue
                  ? false
                  : "P-ISSN OR E-ISSN Number is required",
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Enter P-ISSN number"
                  type="text"
                  id="pissn_no"
                />
              )}
            />
            {errors.pissn_no && typeof errors.pissn_no.message === "string" && (
              <div className="error-text">{errors.pissn_no.message}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label for="first_volume">First Volume</Label>
            <Controller
              name="first_volume"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Enter first volume"
                  type="text"
                  id="first_volume"
                />
              )}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <Controller
            name="number_of_issue_per_year"
            control={control}
            rules={{
              required: "Number of Issues Per Year is required",
            }}
            render={({ field }) => (
              <IssuesSelector
                name="number_of_issue_per_year"
                label="Number of Issues Per Year"
                value={field.value}
                onChange={field.onChange}
                control={control}
              />
            )}
          />
          {errors.number_of_issue_per_year &&
            typeof errors.number_of_issue_per_year.message === "string" && (
              <div className="error-text">
                {errors.number_of_issue_per_year.message}
              </div>
            )}
        </Col>

        <Col md="6">
          <FormGroup>
            <Label for="publisher_name">Publisher Name</Label>
            <Controller
              name="publisher_name"
              control={control}
              rules={{
                required: "Publisher Name is required",
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Enter publisher name"
                  type="text"
                  id="publisher_name"
                />
              )}
            />
            {errors.publisher_name &&
              typeof errors.publisher_name.message === "string" && (
                <div className="error-text">
                  {errors.publisher_name.message}
                </div>
              )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label for="website_link">Website Link</Label>
            <Controller
              name="website_link"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Enter website link"
                  type="url"
                  id="website_link"
                />
              )}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="journal_submission_link">Submission Link</Label>
            <Controller
              name="journal_submission_link"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Enter submission link"
                  type="url"
                  id="journal_submission_link"
                />
              )}
            />
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label for="review_type">Review Type</Label>
            <Controller
              name="review_type"
              control={control}
              rules={{
                required: "Review Type is required",
              }}
              render={({ field }) => (
                <Input {...field} type="select" id="review_type">
                  <option value="">-- Select --</option>
                  <option value="Single-Blind">Single-Blind</option>
                  <option value="Double-Blind">Double-Blind</option>
                  <option value="Open Peer Review">Open Peer Review</option>
                  <option value="Collaborative">Collaborative</option>
                </Input>
              )}
            />
            {errors.review_type &&
              typeof errors.review_type.message === "string" && (
                <div className="error-text">{errors.review_type.message}</div>
              )}
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="review_time">Review Time</Label>
            <Controller
              name="review_time"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Enter review time"
                  type="text"
                  id="review_time"
                />
              )}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="country">Country</Label>
            <Controller
              name="country"
              control={control}
              rules={{
                required: "Country is required",
              }}
              render={({ field }) => (
                <CountrySelector
                  {...field}
                  currentCountry={field.value}
                  onSelectCountryCallback={(value: number) => {
                    field.onChange(value.toString());
                    setValue("country", value.toString());
                  }}
                />
              )}
            />
            {errors.country && typeof errors.country.message === "string" && (
              <div className="error-text">{errors.country.message}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label for="state">State</Label>
            <Controller
              name="state"
              control={control}
              rules={{
                required: "State is required",
              }}
              render={({ field }) => (
                <StateSelector
                  {...field}
                  currentState={field.value}
                  countryId={getValues("country")}
                  onSelectStateCallback={(value: string) => {
                    setValue("state", value);
                  }}
                />
              )}
            />
            {errors.state && typeof errors.state.message === "string" && (
              <div className="error-text">{errors.state.message}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label for="broad_research_area">Broad Research Area</Label>
            <Controller
              name="broad_research_area"
              control={control}
              rules={{
                required: "Broad Research Area is required",
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Enter broad research area"
                  type="text"
                  id="broad_research_area"
                />
              )}
            />
            {errors.broad_research_area &&
              typeof errors.broad_research_area.message === "string" && (
                <div className="error-text">
                  {errors.broad_research_area.message}
                </div>
              )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label for="indexing">Indexing</Label>
            <Controller
              name="indexing"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Enter indexing"
                  type="text"
                  id="indexing"
                />
              )}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="publication_type">Publication Type</Label>
            <Controller
              name="publication_type"
              control={control}
              rules={{
                required: "Publication Type is required",
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  type="select"
                  id="publication_type"
                  onChange={(e) => {
                    if (e.target.value == "Free") {
                      setValue("usd_publication_charge", "0");
                    }
                    setValue("publication_type", e.target.value);
                  }}
                >
                  <option value="">-- Select --</option>
                  <option value="Free">Free</option>
                  <option value="Paid">Paid</option>
                </Input>
              )}
            />
            {errors.publication_type &&
              typeof errors.publication_type.message === "string" && (
                <div className="error-text">
                  {errors.publication_type.message}
                </div>
              )}
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="usd_publication_charge">Publication Charge</Label>
            <Controller
              name="usd_publication_charge"
              control={control}
              rules={{
                validate: (value) => {
                  return /^[0-9]+$/.test(value) || "Invalid value entered.";
                },
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  readOnly={publicationType == "Free"}
                  placeholder="Enter publication charge"
                  type="number"
                  id="usd_publication_charge"
                />
              )}
            />
            {errors.usd_publication_charge &&
              typeof errors.usd_publication_charge.message === "string" && (
                <div className="error-text">
                  {errors.usd_publication_charge.message}
                </div>
              )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label for="image">Upload Journal Image</Label>
            <Controller
              name="image"
              control={control}
              rules={{
                required: "Journal image is required",
              }}
              render={({ field }) => (
                <>
                  <Input
                    type="file"
                    id="image"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        field.onChange(file); // Set value properly
                        setPreviewImage(URL.createObjectURL(file));
                      }
                    }}
                  />
                  {errors.image && (
                    <div className="error-text">{errors.image.message}</div>
                  )}
                </>
              )}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="impact_factor">Impact Factor</Label>
            <Controller
              name="impact_factor"
              control={control}
              // rules={{
              //   validate: (value) => {
              //     return /^[a-zA-Z0-9]+$/.test(value)
              //       ? true
              //       : "Invalid Value Entered";
              //   },
              // }}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  id="impact_factor"
                  placeholder="Enter impact factor"
                />
              )}
            />
            {errors.impact_factor && (
              <div className="error-text">{errors.impact_factor.message}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label for="language">Language</Label>
            <Controller
              name="language"
              control={control}
              rules={{ required: "Language is required" }}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  id="language"
                  placeholder="Enter language"
                />
              )}
            />
            {errors.language && (
              <div className="error-text">{errors.language.message}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label for="doi">DOI</Label>
            <Controller
              name="doi"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  id="doi"
                  placeholder="Enter DOI"
                />
              )}
            />
            {errors.doi && (
              <div className="error-text">{errors.doi.message}</div>
            )}
          </FormGroup>
        </Col>
        <Col md="12">
          <FormGroup>
            <Label for="description">Description</Label>
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <ReactQuill
                  {...field}
                  value={field.value || ""}
                  id="description"
                  onChange={(content) => {
                    const cleanText = new DOMParser().parseFromString(
                      content,
                      "text/html"
                    ).body.innerText;
                    field.onChange(content);
                  }}
                  placeholder="Enter Description"
                />
              )}
            />
          </FormGroup>
        </Col>
      </Row>
      <div className="text-center mt-4">{submitButton}</div>
    </Form>
  );
};
